/**
 * A conversa de um feedback — spec 0108.
 *
 * A mesma para quem enviou e para quem atende. O modo muda o que aparece:
 * quem atende vê o contexto do envio, as notas internas e pode marcar a
 * resposta como nota; quem enviou nunca recebe a nota (o servidor nem a manda).
 */
import { useEffect, useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { quando } from '../../utils/formato';
import {
  ROTULO_CANAL, MAX_ANEXOS, ACEITA_NO_SELETOR, motivoDaRecusa, tamanhoLegivel, urlDoArquivo,
  type FeedbackDetalhe, type ArquivoVisto,
} from './api';

interface Props {
  detalhe: FeedbackDetalhe;
  modo: 'autor' | 'equipe';
  /** Envia a mensagem; devolve quantos anexos não chegaram. */
  onResponder: (texto: string, nota: boolean, anexos: File[]) => Promise<number>;
}

/** "Eu" fica sempre à direita: a pessoa, na visão dela; a equipe, na fila. */
function classeDa(autor: 'PESSOA' | 'EQUIPE' | 'NOTA' | 'EVENTO', modo: 'autor' | 'equipe'): string {
  if (autor === 'NOTA') return 'fb-msg-nota';
  if (autor === 'EVENTO') return 'fb-msg-evento';
  const minha = (autor === 'PESSOA') === (modo === 'autor');
  return minha ? 'fb-msg-minha' : 'fb-msg-outro';
}

function Arquivo({ a }: { a: ArquivoVisto }) {
  const [erro, setErro] = useState<string | null>(null);
  if (a.situacao === 'EXPURGADO') return <span className="fb-arquivo-apagado">{a.nome} · apagado</span>;
  if (a.situacao === 'PENDENTE') return <span className="fb-arquivo-apagado">{a.nome} · não chegou</span>;
  const abrir = async () => {
    setErro(null);
    try {
      window.open(await urlDoArquivo(a.idArquivo), '_blank', 'noopener');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível abrir.');
    }
  };
  return (
    <span className="fb-arquivo-link">
      <button type="button" className="fc-btn-link" onClick={abrir}>
        <Paperclip size={14} aria-hidden="true" /> {a.nome}
      </button>
      <small>{tamanhoLegivel(a.tamanho)}</small>
      {erro && <small className="fb-erro" role="alert">{erro}</small>}
    </span>
  );
}

/** A captura, carregada pelo link de 5 minutos só quando a conversa abre. */
function Captura({ a }: { a: ArquivoVisto }) {
  const [url, setUrl] = useState<string | null>(null);
  const [falhou, setFalhou] = useState(false);
  useEffect(() => {
    let vivo = true;
    if (a.situacao === 'ENVIADO') urlDoArquivo(a.idArquivo).then((u) => vivo && setUrl(u)).catch(() => vivo && setFalhou(true));
    return () => { vivo = false; };
  }, [a.idArquivo, a.situacao]);
  if (a.situacao === 'EXPURGADO') return <p className="fb-ajuda">A captura foi apagada 90 dias depois de resolvido.</p>;
  if (a.situacao === 'PENDENTE' || falhou) return <p className="fb-ajuda">A captura não chegou ao armazenamento.</p>;
  if (!url) return <div className="fb-captura fb-captura-carregando" aria-busy="true" aria-label="Carregando a captura" />;
  return (
    <a className="fb-captura" href={url} target="_blank" rel="noopener noreferrer" aria-label="Abrir a captura da tela em tamanho real">
      <img src={url} alt="Captura da tela enviada com o feedback" />
    </a>
  );
}

export function Conversa({ detalhe, modo, onResponder }: Props) {
  const [texto, setTexto] = useState('');
  const [nota, setNota] = useState(false);
  const [anexos, setAnexos] = useState<File[]>([]);
  const [recusa, setRecusa] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const seletor = useRef<HTMLInputElement>(null);

  const captura = detalhe.arquivos.find((a) => a.papel === 'CAPTURA');
  const anexosDoEnvio = detalhe.arquivos.filter((a) => a.papel === 'ANEXO' && a.idMensagem === null);
  const anexosDe = (idMensagem: number) => detalhe.arquivos.filter((a) => a.idMensagem === idMensagem);
  const conversa = detalhe.mensagens;

  const escolher = (lista: FileList | null) => {
    if (!lista) return;
    const novos = [...anexos];
    const recusados: string[] = [];
    for (const f of Array.from(lista)) {
      const motivo = motivoDaRecusa(f) ?? (novos.length >= MAX_ANEXOS ? `Já há ${MAX_ANEXOS} anexos` : null);
      if (motivo) recusados.push(`${f.name}: ${motivo}`); else novos.push(f);
    }
    setAnexos(novos);
    setRecusa(recusados.length ? recusados.join(' · ') : null);
    if (seletor.current) seletor.current.value = '';
  };

  const enviar = async () => {
    if (!texto.trim()) { setErro('Escreva a mensagem antes de enviar.'); return; }
    setEnviando(true); setErro(null); setAviso(null);
    try {
      const faltaram = await onResponder(texto.trim(), nota, anexos);
      setTexto(''); setAnexos([]); setNota(false); setRecusa(null);
      if (faltaram > 0) {
        setAviso(faltaram === 1
          ? 'A mensagem foi, mas 1 anexo não chegou. Anexe de novo numa nova mensagem.'
          : `A mensagem foi, mas ${faltaram} anexos não chegaram. Anexe de novo numa nova mensagem.`);
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível enviar.');
    } finally {
      setEnviando(false);
    }
  };

  const rotuloCampo = modo === 'equipe' ? (nota ? 'Nota interna' : 'Responder') : 'Acrescentar algo';

  return (
    <div className="fb-conversa">
      <p className="fb-original">{detalhe.mensagem}</p>

      <dl className="fb-contexto">
        {modo === 'equipe' && (
          <>
            <dt>Quem</dt><dd>{detalhe.nomeAutor ?? '—'}{detalhe.perfil ? ` · ${detalhe.perfil}` : ''}</dd>
            <dt>Canal</dt><dd>{ROTULO_CANAL[detalhe.canal]}</dd>
          </>
        )}
        <dt>Tela</dt><dd>{detalhe.tela ?? '—'}</dd>
        {modo === 'equipe' && <><dt>Endereço</dt><dd>{detalhe.endereco ?? '—'}</dd></>}
        {modo === 'equipe' && detalhe.assumidoPor && <><dt>Com</dt><dd>{detalhe.assumidoPor}</dd></>}
      </dl>

      {captura && <Captura a={captura} />}
      {anexosDoEnvio.length > 0 && (
        <div className="fb-anexos-vistos">{anexosDoEnvio.map((a) => <Arquivo key={a.idArquivo} a={a} />)}</div>
      )}

      <section className="fb-secao" aria-labelledby="fb-conversa-titulo">
        <h3 id="fb-conversa-titulo" className="fb-rotulo">Conversa</h3>
        {conversa.length === 0 ? (
          <p className="fb-ajuda">
            {modo === 'autor'
              ? 'A equipe ainda não respondeu. Quando responder, o botão Feedback mostra um número.'
              : 'Ninguém respondeu ainda.'}
          </p>
        ) : (
          <ol className="fb-mensagens">
            {conversa.map((m) => (
              <li key={m.idMensagem} className={`fb-msg ${classeDa(m.autorTipo, modo)}`}>
                {m.autorTipo === 'EVENTO' ? (
                  <span>{m.texto} · {quando(m.criadoEm)}</span>
                ) : (
                  <>
                    <header>
                      <span>{m.autorTipo === 'PESSOA' && modo === 'autor' ? 'Você' : m.autor}{m.autorTipo === 'NOTA' ? ' · nota interna, só a equipe vê' : ''}</span>
                      <time dateTime={m.criadoEm}>{quando(m.criadoEm)}</time>
                    </header>
                    <p>{m.texto}</p>
                    {anexosDe(m.idMensagem).map((a) => <Arquivo key={a.idArquivo} a={a} />)}
                  </>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="fb-responder" aria-label={rotuloCampo}>
        <label className="fb-campo">
          <span className="fb-rotulo">{rotuloCampo}</span>
          <textarea
            value={texto}
            maxLength={5000}
            onChange={(e) => { setTexto(e.target.value); if (erro) setErro(null); }}
            placeholder={modo === 'autor' ? 'Acrescentar algo para a equipe…' : nota ? 'Só a equipe vai ler…' : `Escreva a resposta para ${detalhe.nomeAutor ?? 'quem enviou'}…`}
            aria-invalid={erro ? true : undefined}
            aria-describedby={erro ? 'fb-resposta-erro' : undefined}
          />
        </label>
        {erro && <span className="fb-erro" id="fb-resposta-erro" role="alert">{erro}</span>}
        {aviso && <span className="fb-aviso" role="status">{aviso}</span>}
        {modo === 'autor' && detalhe.status === 'RESOLVIDO' && (
          <span className="fb-ajuda">Este feedback está resolvido. Escrever de novo o devolve à equipe.</span>
        )}
        {anexos.length > 0 && (
          <ul className="fb-arquivos">
            {anexos.map((a, i) => (
              <li key={`${a.name}-${i}`} className="fb-arquivo">
                <span>{a.name}</span><small>{tamanhoLegivel(a.size)}</small>
                <button type="button" className="fc-btn-icone" aria-label={`Tirar ${a.name}`} onClick={() => setAnexos(anexos.filter((_, k) => k !== i))}>
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
        {recusa && <span className="fb-erro" role="alert">{recusa}</span>}
        <div className="fb-responder-acoes">
          <div className="fb-responder-extras">
            <button type="button" className="fc-btn fc-btn-secundario" onClick={() => seletor.current?.click()} disabled={enviando || anexos.length >= MAX_ANEXOS}>
              <Paperclip size={14} aria-hidden="true" /> Anexar arquivo
            </button>
            <input ref={seletor} type="file" multiple hidden accept={ACEITA_NO_SELETOR} onChange={(e) => escolher(e.target.files)} />
            {modo === 'equipe' && (
              <label className="fb-check">
                <input type="checkbox" checked={nota} onChange={(e) => setNota(e.target.checked)} />
                Nota interna (só a equipe vê)
              </label>
            )}
          </div>
          <button type="button" className="fc-btn fc-btn-primario" onClick={enviar} disabled={enviando}>
            {enviando ? 'Enviando…' : modo === 'equipe' ? (nota ? 'Gravar nota' : 'Responder') : 'Enviar'}
          </button>
        </div>
      </section>
    </div>
  );
}
